/** @format */

import {
	ALREADY_SCANNED,
	FOUND,
	NOTFOUND,
	NOT_VALID_YET,
	NOT_VALID_ANYMORE,
	FLAG_RED,
	FLAG_GREEN,
	FLAG_YELLOW,
} from "constants/scanTypes";
import moment, { isMoment } from "moment/moment";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useContext } from "react";
import { useState } from "react";
import { encodeQueryParams } from "util/api";
import { NetworkStatusContext } from "./networkStatus";
import { useLocalStorage } from "hooks/localStorage";
import { Alert } from "@mantine/core";
import {
	DBResponse,
	getBoothscanDataFromDatabase,
	getOfflineTicketsAmountFromDatabase,
	getScansAmountFromDatabase,
	getTicketListFromDatabase,
	getTicketsAmountFromDatabase,
	getTicketValidationDataFromDatabase,
	getUniqueScansAmountFromDatabase,
	requestLastScanIdFromIndexedDb,
	writeOfflinePassScanToDatabase,
	writeOfflineTicketToDatabase,
	writeScansToIndexedDb,
} from "indexedDb/operations";
import { useInterval } from "@mantine/hooks";
import { DB_RESPONSE_ERROR, DB_RESPONSE_OK } from "constants/dbStatuses";
import { scansUrl, ticketsUrl } from "constants/apiurls";
import { ConfigurationsContext } from "./configurations";
import { Localized } from "@fluent/react";
import { IconCheck, IconInfoCircle, IconTimes } from "Components/icons/icons";
import { useNotificationContext } from "./notificationContext";
const { createContext } = require("react");

export const TicketStorageContext = createContext({});

const TicketStorageContextProvider = ({ children }) => {
	const [lastSync, setLastSync] = useLocalStorage("liveto-scanner-tickets-sync-timestamp", null);
	// @ts-expect-error improperly typed context
	const { scanSettings, boothMode, scanMode, scannerConfiguration } = useContext(ConfigurationsContext);
	const [ticketsAmount, setTicketsAmount] = useState(0);
	const [scansAmount, setScansAmount] = useState(0);
	const [uniqueScansAmount, setUniqueScansAmount] = useState(0);
	const [offlineTicketsAmount, setOfflineTicketsAmount] = useState(0);
	const [initialized, setInitialized] = useState(false);
	// @ts-expect-error improperly typed context
	const { handleErrorNotification, handleSuccessNotification } = useNotificationContext();
	const online = useContext(NetworkStatusContext);

	//https://stackoverflow.com/questions/57847594/react-hooks-accessing-up-to-date-state-from-within-a-callback
	const onlineRef = useRef(online);
	useEffect(() => {
		onlineRef.current = online;
	}, [online]);

	const modeRef = useRef(scanMode.mode);
	useEffect(() => {
		modeRef.current = scanMode.mode;
	}, [scanMode.mode]);

	const section = useMemo(() => (scanMode.section ? parseInt(scanMode.section) : null), [scanMode.section]);
	const sectionRef = useRef(section);
	useEffect(() => {
		sectionRef.current = section;
	}, [section]);
	// -----------------------------------------------------

	// To not make the scanner dog slow, we only post scans immediately in booth mode.
	// Normal scans are added to idb and then synced in batch in background
	const persistScanToIndexedDb = useCallback(
		async scanData => {
			const response = await writeOfflineTicketToDatabase(scanData);
			if (response.status === DB_RESPONSE_OK) {
				handleSuccessNotification({
					title: <Localized id="scanview-notification-innermemory-success-title">Success!</Localized>,
					children: (
						<Localized id="scanview-notification-innermemory-success-body">
							{"Scan stored to device memory"}
						</Localized>
					),
				});
			}
			if (response.status === DB_RESPONSE_ERROR) {
				handleErrorNotification({
					title: <Localized id="scanview-notification-innermemory-error-title">Error!</Localized>,
					children: (
						<Localized id="scanview-notification-innermemory-error-body">
							{"Could not store the scan to device memory"}
						</Localized>
					),
				});
			}
		},
		[handleErrorNotification, handleSuccessNotification],
	);

	// This should only be used in booth mode or otherwise when it would be
	// catastrophic to lose a scan
	const persistScanToBackend = useCallback(
		async scanData => {
			if (!onlineRef.current) {
				// fall back to idb
				await persistScanToIndexedDb(scanData);
				return;
			}
			const ticketToScan = { ...scanData };

			if (!ticketToScan.section_id) {
				delete ticketToScan.section_id;
			}
			// If online, post the new scan to backend

			const postTicketsResponse = await fetch(scansUrl, {
				method: "POST",
				headers: { "Content-Type": "Application/json" },
				body: JSON.stringify({
					apikey: scannerConfiguration.apikey,
					tickets: [ticketToScan],
					device: scanSettings.device_name || "Unnamed device",
				}),
			});

			// if (postTicketsResponse.ok && !boothMode) {
			// 	const lastScanResponse = await requestLastScanIdFromIndexedDb();

			// 	if (lastScanResponse.status === DB_RESPONSE_OK) {
			// 		// Fetch the new scans
			// 		const scansResponse = await fetch(
			// 			`${scansUrl}${encodeQueryParams({
			// 				apikey: scannerConfiguration.apikey,
			// 				after: lastScanResponse.data,
			// 			})}`,
			// 		);
			// 		if (scansResponse.ok) {
			// 			const scansData = await scansResponse.json();
			// 			if (scansData.length > 0) {
			// 				await writeScansToIndexedDb(scansData);
			// 			}
			// 		} else {
			// 			const errorData = await scansResponse.json();
			// 			handleErrorNotification({
			// 				title: (
			// 					<Localized
			// 						id="apirequest-error-newscans"
			// 						vars={{ status: scansResponse.status }}
			// 					>{`Error fetching new scans: ${scansResponse.status}`}</Localized>
			// 				),
			// 				children: JSON.stringify(errorData),
			// 			});
			// 		}
			// 	}
			// }

			if (!postTicketsResponse.ok) {
				const errorJson = await postTicketsResponse.json();
				handleErrorNotification({
					title: (
						<Localized
							id="scanview-notification-booth-error-title"
							vars={{ status: postTicketsResponse.status }}
						>{`Error: ${postTicketsResponse.status}`}</Localized>
					),
					children: JSON.stringify(errorJson),
				});
			} else {
				handleSuccessNotification({
					title: <Localized id="scanview-read-success">Success!</Localized>,
					children: (
						<Localized id="scanview-notification-booth-success-body">Contact saved successfully</Localized>
					),
				});
			}
		},
		[
			boothMode,
			handleErrorNotification,
			handleSuccessNotification,
			scannerConfiguration.apikey,
			scanSettings.device_name,
			persistScanToIndexedDb,
		],
	);

	// Season pass scans go to their own offline queue (own id space + own
	// upload endpoint); same store-locally-sync-later model as tickets.
	const persistPassScanToIndexedDb = useCallback(
		async passScanData => {
			const response = await writeOfflinePassScanToDatabase(passScanData);
			if (response.status === DB_RESPONSE_OK) {
				handleSuccessNotification({
					title: <Localized id="scanview-notification-innermemory-success-title">Success!</Localized>,
					children: (
						<Localized id="scanview-notification-innermemory-success-body">
							{"Scan stored to device memory"}
						</Localized>
					),
				});
			}
			if (response.status === DB_RESPONSE_ERROR) {
				handleErrorNotification({
					title: <Localized id="scanview-notification-innermemory-error-title">Error!</Localized>,
					children: (
						<Localized id="scanview-notification-innermemory-error-body">
							{"Could not store the scan to device memory"}
						</Localized>
					),
				});
			}
			return response;
		},
		[handleErrorNotification, handleSuccessNotification],
	);

	// Operator decision on a same-day pass rescan: accept OR reject, both are
	// recorded (a rejected rescan is signal, not noise).
	const manualVerifyPass = useCallback(
		async (passId, accepted, metaData) => {
			return await persistPassScanToIndexedDb({
				season_pass_id: passId,
				scanned: moment.utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
				accepted,
				device_label: scanSettings.device_name || "Unnamed device",
				metadata: {
					...metaData,
					manual: true,
				},
			});
		},
		[scanSettings.device_name, persistPassScanToIndexedDb],
	);

	const manualVerifyTicket = useCallback(
		async (ticketId, metaData, direction) => {
			const type = modeRef.current === "entry" || modeRef.current === "exit" ? direction : "section";
			const scanningData = {
				ticket_id: ticketId,
				scanned: moment.utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
				scan_type: type,
				section_id: sectionRef.current,
				device_id: scanSettings.device_name || "Unnamed device",
				device_label: scanSettings.device_name || "Unnamed device",
				metadata: {
					...metaData,
					direction: direction,
					manual: true,
				},
			};

			return await persistScanToIndexedDb(scanningData);
		},
		[scanSettings.device_name, modeRef, sectionRef, persistScanToIndexedDb],
	);

	const updateTicketAmountFromDatabaseToState = useCallback(
		async (hint?: number | ((prevState: number) => number) | undefined) => {
			if (hint !== undefined) {
				setTicketsAmount(hint);
				return;
			}
			const getResponse = await getTicketsAmountFromDatabase();
			if (getResponse.status === DB_RESPONSE_OK) {
				setTicketsAmount(getResponse.data);
			}
		},
		[],
	);

	const updateUniqueScansAmountFromDatabaseToState = useCallback(
		async (hint?: number | ((prevState: number) => number) | undefined) => {
			if (hint !== undefined) {
				setUniqueScansAmount(hint);
				return;
			}
			const getResponse = await getUniqueScansAmountFromDatabase();
			if (getResponse.status === DB_RESPONSE_OK) {
				setUniqueScansAmount(getResponse.data);
			}
		},
		[],
	);

	const updateUnsyncedScanAmountFromDatabase = useCallback(
		async (hint?: number | ((prevState: number) => number) | undefined) => {
			if (hint !== undefined) {
				setOfflineTicketsAmount(hint);
				return;
			}
			const getResponse = await getOfflineTicketsAmountFromDatabase();
			if (getResponse.status === DB_RESPONSE_OK) {
				setOfflineTicketsAmount(getResponse.data);
			}
		},
		[],
	);

	const updateTotalScansAmountFromDatabaseToState = useCallback(
		async (hint?: number | ((prevState: number) => number) | undefined) => {
			if (hint !== undefined) {
				setScansAmount(hint);
				return;
			}
			const getResponse = await getScansAmountFromDatabase();
			if (getResponse.status === DB_RESPONSE_OK) {
				setScansAmount(getResponse.data);
			}
		},
		[],
	);

	useEffect(() => {
		Promise.all([
			updateTicketAmountFromDatabaseToState(),
			updateUniqueScansAmountFromDatabaseToState(),
			updateUnsyncedScanAmountFromDatabase(),
			updateTotalScansAmountFromDatabaseToState(),
		]).then(() => setInitialized(true));
	}, [
		updateTicketAmountFromDatabaseToState,
		updateUniqueScansAmountFromDatabaseToState,
		updateUnsyncedScanAmountFromDatabase,
		updateTotalScansAmountFromDatabaseToState,
	]);

	const ctx = useMemo(
		() => ({
			initialized,
			lastSync,
			setLastSync,
			updateTicketAmountFromDatabaseToState,
			updateUniqueScansAmountFromDatabaseToState,
			updateUnsyncedScanAmountFromDatabase,
			ticketsAmount,
			scansAmount,
			uniqueScansAmount,
			offlineTicketsAmount,
			manualVerifyTicket,
			manualVerifyPass,
			persistScanToIndexedDb,
			persistPassScanToIndexedDb,
			persistScanToBackend,
		}),
		[
			initialized,
			lastSync,
			setLastSync,
			updateTicketAmountFromDatabaseToState,
			updateUniqueScansAmountFromDatabaseToState,
			updateUnsyncedScanAmountFromDatabase,
			ticketsAmount,
			scansAmount,
			uniqueScansAmount,
			offlineTicketsAmount,
			manualVerifyTicket,
			manualVerifyPass,
			persistScanToIndexedDb,
			persistPassScanToIndexedDb,
			persistScanToBackend,
		],
	);
	return <TicketStorageContext.Provider value={ctx}>{children}</TicketStorageContext.Provider>;
};

export default TicketStorageContextProvider;
